import { useState, useCallback, useRef } from 'react';
import { cleanTextForSpeech } from '@/utils/textCleaner';
import { supabase } from '@/integrations/supabase/client';

interface UseElevenLabsTTSOptions {
  voiceId?: string;
  language?: string;
  onError?: (error: Error) => void;
  onStart?: () => void;
  onEnd?: () => void;
}

interface UseElevenLabsTTSReturn {
  speak: (text: string) => Promise<void>;
  stop: () => void;
  isPlaying: boolean;
  isLoading: boolean;
  error: Error | null;
}

export const useElevenLabsTTS = (options: UseElevenLabsTTSOptions = {}): UseElevenLabsTTSReturn => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioUrlRef = useRef<string | null>(null);

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
    setIsPlaying(false);
    setIsLoading(false);
  }, []);

  const speak = useCallback(async (text: string) => {
    if (!text || text.length < 5) return;

    // Stop any ongoing playback
    stop();
    setError(null);
    setIsLoading(true);

    try {
      const cleanedText = cleanTextForSpeech(text);
      
      // Call ElevenLabs TTS edge function via Supabase
      const { data, error: invokeError } = await supabase.functions.invoke('elevenlabs-tts', {
        body: {
          text: cleanedText,
          voiceId: options.voiceId,
          language: options.language || 'ru',
        },
      });

      if (invokeError) {
        throw new Error(invokeError.message || 'TTS request failed');
      }

      // Check if response is an error object
      if (data?.error) {
        throw new Error(data.error);
      }

      // Supabase functions.invoke returns blob for binary responses
      let audioBlob: Blob;
      if (data instanceof Blob) {
        audioBlob = data;
      } else if (data instanceof ArrayBuffer) {
        audioBlob = new Blob([data], { type: 'audio/mpeg' });
      } else {
        // Fallback: try to get as array buffer
        console.error('[ElevenLabs TTS] Unexpected response type:', typeof data);
        throw new Error('Invalid audio response format');
      }

      const audioUrl = URL.createObjectURL(audioBlob);
      audioUrlRef.current = audioUrl;

      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      audio.onplay = () => {
        setIsPlaying(true);
        setIsLoading(false);
        options.onStart?.();
      };

      audio.onended = () => {
        setIsPlaying(false);
        if (audioUrlRef.current) {
          URL.revokeObjectURL(audioUrlRef.current);
          audioUrlRef.current = null;
        }
        options.onEnd?.();
      };

      audio.onerror = () => {
        const err = new Error('Audio playback failed');
        setError(err);
        setIsPlaying(false);
        setIsLoading(false);
        options.onError?.(err);
      };

      await audio.play();

    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown TTS error');
      console.error('[ElevenLabs TTS] Error:', error);
      setError(error);
      setIsLoading(false);
      options.onError?.(error);
    }
  }, [options, stop]);

  return {
    speak,
    stop,
    isPlaying,
    isLoading,
    error,
  };
};
